import styled, { css } from 'styled-components';

import { Text } from '@/nova/components';
import { CFG, CFG_ANIM, CFG_CHROME } from '../../../../../theme/configurator-tokens';
import { thinScrollbar } from '../../../../../theme/configurator-keyframes';

/** Travel direction of a step change: `forward` (next step), `back` (previous), or
 *  `none` (first paint — fade only, no slide). */
export type StepSlideDir = 'forward' | 'back' | 'none';

const slideOffset = (dir: StepSlideDir): number => {
  if (dir === 'forward') return CFG_ANIM.STEP_SLIDE_OFFSET_PX; // new step arrives from the right
  if (dir === 'back') return -CFG_ANIM.STEP_SLIDE_OFFSET_PX; // arrives from the left
  return 0; // first paint — fade only
};

/**
 * Wraps the main viewport (the left preview pane) so it slides + fades IN on each
 * desktop step change — driven by `MainViewportSlide`. Slide-in only: the previous
 * step's preview unmounts with its route, so there is no outgoing layer (the
 * chosen lightweight behaviour); the sidebar/chrome are never animated.
 *
 * It takes over the main pane's flex role (`flex: 1`) and lets its single child
 * fill it, so the layout is unchanged. The resting transform is `none` (not
 * `translateX(0)`) so it leaves behind no transform containing block once settled.
 * Under `prefers-reduced-motion` the horizontal travel is dropped (fade only).
 */
export const MainSlide = styled.div<{ $dir: StepSlideDir; $entered: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  height: 100%;
  will-change: transform, opacity;
  transition: transform ${CFG_ANIM.STEP_SLIDE_MS}ms ${CFG_ANIM.EASE},
    opacity ${CFG_ANIM.STEP_SLIDE_MS}ms ${CFG_ANIM.EASE};
  opacity: ${({ $entered }) => ($entered ? 1 : 0)};
  transform: ${({ $dir, $entered }) =>
    $entered ? 'none' : `translate3d(${slideOffset($dir)}px, 0, 0)`};

  @media (prefers-reduced-motion: reduce) {
    transition: opacity ${Math.round(CFG_ANIM.STEP_SLIDE_MS / 2)}ms ${CFG_ANIM.EASE};
    transform: none;
  }
`;

export const Shell = styled.div<{ $previewInteractive: boolean }>`
  position: relative;
  display: flex;
  /* Dynamic viewport height, NOT 100vh: on iPad/iOS Safari 100vh resolves to the
     LARGEST viewport (toolbars retracted), so with the tab + address bars shown the
     shell overruns the visible area by the chrome's height. The sidebar's sticky
     footer (the Continue button) is pinned to the shell's bottom, so it lands below
     the fold and becomes unreachable (the Root above clips it via overflow: hidden).
     100dvh tracks the CURRENTLY visible viewport, keeping the footer on-screen. */
  height: 100dvh;
  width: 100%;
  overflow: hidden;
  /* Transparent so the Root's viewport background (v2-parity texture + vignette) shows
     through behind the translucent chrome on every step. */
  background: transparent;
  /* Device-preview step only: the live iframe floats in the persistent host BELOW this
     chrome, and every ancestor over it (this shell, the sliding viewport, the pane)
     would otherwise swallow the wheel. So the whole shell is made click-through and the
     wheel/clicks fall straight to the iframe (the site inside it can be scrolled). The
     interactive islands (LogoSlot, Side, the device switcher) re-enable pointer-events
     on themselves; the empty device anchors stay inert. Other steps keep the chrome
     fully interactive (their main pane carries its own controls). */
  ${({ $previewInteractive }) =>
    $previewInteractive &&
    css`
      pointer-events: none;
    `}
`;

/**
 * Fixed brand lockup, top-left. Lives at the Shell level — a sibling of the sliding
 * main viewport, NOT inside it — so it stays put while each step's preview slides in.
 * (It used to sit inside every step's main pane, so it jittered along with the slide.)
 * Positioned at the same 32/32 inset the per-step panes used, so the logo doesn't move.
 * z-index keeps it above the resting device host (PreviewStage Layer, z-index 0) and
 * below the full-screen Live Preview, which raises that host above everything.
 */
export const LogoSlot = styled.div`
  position: absolute;
  left: 32px;
  top: 32px;
  z-index: 10;
  /* Stays interactive when the device-preview Shell goes click-through. */
  pointer-events: auto;
`;

export const Side = styled.aside`
  position: relative;
  margin: 16px 16px 16px 0;
  display: flex;
  width: 390px;
  flex-shrink: 0;
  flex-direction: column;
  overflow: hidden;
  border-radius: 20px;
  border: 1px solid ${CFG_CHROME.sidebarBorder};
  background: ${CFG_CHROME.sidebarBg};
  /* Stays interactive when the device-preview Shell goes click-through. */
  pointer-events: auto;
`;

export const Scroll = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  overflow-y: auto;
  ${thinScrollbar};
`;

export const Body = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  padding: 12px 12px 16px;
`;

export const Intro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Title = styled(Text)`
  font-size: 18px;
  font-weight: 900;
  line-height: 1.1;
  color: ${CFG.base200};
`;

export const Description = styled(Text)`
  font-size: 14px;
  line-height: 16px;
  color: ${CFG.base400};
`;
